import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { MediaPreviewPanel, type MediaPreviewPanelHandle, Sheet, SheetContent } from "@nexus/ui"
import type { CreativeHashDataScope } from "@/lib/queries/drilldown.queries"
import type { CompetitorCreativeCardModel } from "../../lib/drilldown-card.types"
import { mapCompetitorToDrawerModel } from "../../lib/drilldown-drawer-model"
import { mapDrawerNarrativeContent } from "../../lib/drawer-narrative-content"
import { parseCreativeHashDataRows } from "../../lib/parse-creative-hash-data"
import {
  isIncludedCarouselSlide,
  shouldKeepDrilldownDrawerOpenOnOutsideInteraction,
} from "../../lib/drilldown-drawer-utils"
import { useCreativeHashDataSection } from "../../hooks/useCreativeHashData"
import { DrilldownCreativeInfoTab } from "./DrilldownCreativeInfoTab"
import { DrilldownCreativeLinkMenu } from "./DrilldownCreativeLinkMenu"

export interface DrilldownCreativeDrawerProps {
  /** The page opening the drawer; the Info tab reads through that page's API prefix. */
  scope: CreativeHashDataScope
  creative: CompetitorCreativeCardModel | null
  open: boolean
  onOpenChange: (open: boolean) => void
  startDate: string
  endDate: string
  dateRange: string
}

export function DrilldownCreativeDrawer({
  scope,
  creative,
  open,
  onOpenChange,
  startDate,
  endDate,
  dateRange,
}: DrilldownCreativeDrawerProps) {
  const { t } = useTranslation()
  const [carouselSlideIndex, setCarouselSlideIndex] = useState(0)
  const mediaPreviewRef = useRef<MediaPreviewPanelHandle>(null)
  const model = useMemo(() => (creative ? mapCompetitorToDrawerModel(creative) : null), [creative])

  useEffect(() => {
    setCarouselSlideIndex(0)
  }, [creative?.id])

  const isIncludedSlide = isIncludedCarouselSlide(creative, carouselSlideIndex)
  const shouldFetchHashData = open && !isIncludedSlide
  const creativeHashData = useCreativeHashDataSection(
    scope,
    creative,
    shouldFetchHashData,
    startDate,
    endDate,
    dateRange,
  )

  const handleCarouselSlideChange = useCallback((index: number) => {
    setCarouselSlideIndex(index)
  }, [])

  const parsed = useMemo(() => {
    if (isIncludedSlide) return null
    return creativeHashData.section?.rows.length
      ? parseCreativeHashDataRows(creativeHashData.section.rows)
      : null
  }, [creativeHashData.section?.rows, isIncludedSlide])
  const narrative = useMemo(() => mapDrawerNarrativeContent(parsed), [parsed])

  const handleSequenceSeek = useCallback((seconds: number) => {
    mediaPreviewRef.current?.seekTo(seconds)
  }, [])

  const activeSlide = model?.carouselSlides?.[carouselSlideIndex]

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        closePosition="left"
        className="flex w-full flex-col overflow-hidden bg-card p-0 sm:max-w-[1080px]"
        onPointerDownOutside={(e) => {
          if (shouldKeepDrilldownDrawerOpenOnOutsideInteraction(e.target)) {
            e.preventDefault()
          }
        }}
        onInteractOutside={(e) => {
          if (shouldKeepDrilldownDrawerOpenOnOutsideInteraction(e.target)) {
            e.preventDefault()
          }
        }}
      >
        {model ? (
          <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
            <div className="flex min-h-0 w-full shrink-0 flex-col border-border bg-muted/40 sm:w-[44%] sm:border-r">
              <div className="border-b border-border px-6 py-4 sm:hidden">
                <DrawerTabList label={t("drilldown.drawer.infoTab", "Info")} />
              </div>
              <MediaPreviewPanel
                ref={mediaPreviewRef}
                key={model.id}
                contentKey={model.id}
                src={model.isCarousel ? undefined : model.assetUrl || undefined}
                thumbnailSrc={model.thumbnailUrl}
                type={model.isVideo ? "video" : "image"}
                alt={model.brandName}
                slides={model.carouselSlides}
                renderFooterActions={({ activeSrc }) => (
                  <DrilldownCreativeLinkMenu
                    assetUrl={activeSrc ?? ""}
                    adId={model.adId}
                    creativeId={activeSlide?.hash ?? model.hash}
                    adLibUrl={model.adLibUrl}
                  />
                )}
                onSlideChange={model.isCarousel ? handleCarouselSlideChange : undefined}
                className="min-h-[320px] flex-1"
              />
            </div>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
              <div className="hidden border-b border-border px-6 py-4 sm:block">
                <DrawerTabList label={t("drilldown.drawer.infoTab", "Info")} />
              </div>
              <DrilldownCreativeInfoTab
                model={model}
                narrative={narrative}
                rank={parsed?.rank ?? null}
                isVideo={model.isVideo}
                isHashDataLoading={creativeHashData.isLoading}
                onSequenceSeek={handleSequenceSeek}
              />
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

function DrawerTabList({ label }: { label: string }) {
  return (
    <div className="flex gap-2">
      <span className="inline-flex rounded-lg bg-primary/10 px-3 py-1.5 text-[13px] font-medium text-primary">
        {label}
      </span>
    </div>
  )
}
